import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../app/redux/hooks";
import { paymentCheckoutAsync } from "../../../../../../app/slices/checkoutSlice/CheckoutSlice";
import {
  selectedDeliveryMethod as selectedDeliveryMethodAction,
  selectedDeliveryFee as selectedDeliveryFeeAction,setStepperActiveMenu
} from "../../../../../../app/slices/checkoutSlice/CheckoutSlice";
import { showSnackbar } from "../../../../../../app/slices/common/customSnackBar/customSnackbarSlice";
import { useRef, useState } from "react";
import { setAdditionalInfo } from "../../../../../../app/slices/cartSlice/CartSlice";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";
import RenderRazorpay from "./RazorPay";
import axios from "../../../../../../axios";
import { useEffect } from "react";
import { PAYU_URL } from "../../../../../../api/payu/Api";
import { useMediaQuery } from "@mui/material";
import {SelectedAddressMobile} from "../SelectedAddressMobile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CustomDialog from "../../../../../components/custom/dialog";
import { DeliverAddress, isLongDistanceEligibleForAmount } from "../deliveryAddress";
import CustomButton from "../../../../../components/custom/button";
                    const redirectToPayU = (params: any, payuUrl: string) => {
                      const form = document.createElement("form");
                      form.method = "POST";
                      form.action = payuUrl;

                      Object.entries(params).forEach(([key, value]) => {
                        const input = document.createElement("input");
                        input.type = "hidden";
                        input.name = key;
                        input.value = String(value ?? "");
                        form.appendChild(input);
                      });

                      document.body.appendChild(form);
                      form.submit();
                    };


export const PaymentPage = ({ isLongDistance = false }: { isLongDistance?: boolean }) => {
  const isMobileScreen = useMediaQuery("(max-width:765px)");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const selectedAddressId = useAppSelector(
    (state) => state.CheckoutState.selectedAddressId
  );
  const selectedDeliverySlot = useAppSelector(
    (state) => state.CheckoutState.selectedDeliverySlot
  );
  const selectedDeliveryType = useAppSelector(
    (state) => state.CheckoutState.selectedDeliveryType
  );
  const selectedDeliveryDate = useAppSelector(
    (state) => state.CheckoutState.selectedDeliveryDate
  );
  const selectedPickupTime = useAppSelector(
    (state) => state.CheckoutState.selectedPickupTime
  );
  const optIn = useAppSelector((state) => state.CheckoutState.opt_in);
  const { addTocart } = useAppSelector((state) => state.CartState);
  const itemTotal = Number(addTocart?.billingData?.total_without_delivery || 0);
  const isCartEligibleForLongDistance = isLongDistanceEligibleForAmount(itemTotal);
  const { addressList }: any = useAppSelector((state) => state?.AddressState);
  const [selectedPaymentModeId, setSelectedPaymentModeId] = useState<
    number | null
  >(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<string>("");
  const [currentDeliveryFee, setCurrentDeliveryFee] = useState<number>(0);
  const [whatsappOptIn, setWhatsappOptIn] = useState<boolean>(true);
  const [updatedGrandTotal, setUpdatedGrandTotal] = useState<number>(0);
  const [additionalInfoName, setAdditionalInfoName] = useState<string>("");
  const [additionalInfoPhoneNo, setAdditionalInfoPhoneNo] = useState<string>("");
  const [additionalInfoPhoneNoError, setAdditionalInfoPhoneNoError] = useState<string>("");
  const [openAlternateContact, setOpenAlternateContact] = useState(!isMobileScreen);
  const [showLongDistanceAmountDialog, setShowLongDistanceAmountDialog] = useState(false);
  const skipAdditionalInfoPersistence = useRef(false);

  // Initialize alternate contact from cart so it persists when navigating back
  useEffect(() => {
    if (addTocart?.additional_info || addTocart?.phone_no) {
      setAdditionalInfoName(addTocart.additional_info || "");
      setAdditionalInfoPhoneNo(String(addTocart.phone_no || "").slice(0, 10));
      skipAdditionalInfoPersistence.current = true;
    }
  }, [addTocart?.additional_info, addTocart?.phone_no]);

  // Persist alternate contact to cart whenever it changes
  useEffect(() => {
    if (skipAdditionalInfoPersistence.current) {
      skipAdditionalInfoPersistence.current = false;
      return;
    }
    dispatch(setAdditionalInfo({ additional_info: additionalInfoName, phone_no: additionalInfoPhoneNo }));
  }, [additionalInfoName, additionalInfoPhoneNo, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Set default payment mode when available payment modes load
  useEffect(() => {
    const modes = addTocart?.available_payment_modes || [];
    if (modes.length > 0) {
      // prefer the first mode if none selected yet
      if (!selectedPaymentModeId) {
        // For long distance, prefer Online Payment over COD
        if (isLongDistance) {
          const onlinePaymentMode = modes.find((mode: any) => mode.mode_name === "Online Payment");
          setSelectedPaymentModeId(Number(onlinePaymentMode?.id || modes[0].id));
        } else {
          setSelectedPaymentModeId(Number(modes[0].id));
        }
      }
    }
  }, [addTocart?.available_payment_modes, selectedPaymentModeId, isLongDistance]);

  // Set default delivery method to surface when payment method changes
  useEffect(() => {
    if (isLongDistance && selectedPaymentModeId) {
      const defaultMethod = selectedPaymentModeId === 1 ? "surface_cod" : "surface";
      setSelectedDeliveryMethod(defaultMethod);
      const fee = calculateDeliveryFee(selectedPaymentModeId, defaultMethod);
      setCurrentDeliveryFee(fee);
      // Calculate updated grand total: total_without_delivery + new_delivery_fee
      const totalWithoutDelivery = addTocart?.billingData?.total_without_delivery || 0;
      const newGrandTotal = totalWithoutDelivery + fee;
      setUpdatedGrandTotal(newGrandTotal);
      // Dispatch to Redux
      dispatch(selectedDeliveryMethodAction(defaultMethod));
      dispatch(selectedDeliveryFeeAction(fee));
    }
  }, [selectedPaymentModeId, isLongDistance, dispatch]);

  // Calculate delivery fee based on payment and delivery method
  const calculateDeliveryFee = (paymentId: number, deliveryMethod: string) => {
    const charges = (addTocart?.long_distance_charges || {}) as any;

    // New API response can return a single fee field for long-distance delivery.
    // Older API responses used method/payment specific fee keys.
    if (typeof charges?.long_distance_delivery_fee === "number") {
      return charges.long_distance_delivery_fee;
    }

    let fee = 0;
    if (paymentId === 1) {
      // COD
      if (deliveryMethod === "surface_cod") fee = charges.delivery_fee_cod_surface || 0;
      else if (deliveryMethod === "express_cod") fee = charges.delivery_fee_cod_express || 0;
      else if (deliveryMethod === "next_day_cod") fee = charges.delivery_fee_cod_nxt || 0;
    } else {
      // Online Payment
      if (deliveryMethod === "surface") fee = charges.delivery_fee_online_surface || 0;
      else if (deliveryMethod === "express") fee = charges.delivery_fee_online_express || 0;
      else if (deliveryMethod === "next_day") fee = charges.delivery_fee_online_nxt || 0;
    }

    return fee;
  };

  // Get selected address details
  const selectedAddress = addressList?.data?.find(
    (addr: any) => addr.id === selectedAddressId
  );

  const buildFullAddress = (address: any) => {
    if (!address) return "Address will be displayed here";

    const addressParts = [
      address.flat_no,
      address.landmark,
      address.area,
      address.address_3,
      address.street,
      address.address_1,
      address.address_2,
    ].filter((part) => part && String(part).trim() !== "");

    const deduped = Array.from(new Set(addressParts.map((part) => String(part).trim())));
    return deduped.join(", ");
  };

  const displayAddress = buildFullAddress(selectedAddress);
  const hasAddress = !!selectedAddress;
  // Convert delivery method to API format (S, E, N)
  const getDeliveryModeCode = (method: string): string => {
    if (method.includes("surface")) return "S";
    if (method.includes("express")) return "E";
    if (method.includes("next_day")) return "N";
    return "S"; // Default to surface
  };

  // Format selected delivery date (only for non-long distance orders)
  let formattedSelectedDate = new Date().toISOString().split("T")[0]; // Default to today
  if (!isLongDistance && selectedDeliveryDate) {
    try {
      const startDateString = selectedDeliveryDate
        .toString()
        .split(" to ")[0]
        .trim();
      formattedSelectedDate = new Date(startDateString)
        .toISOString()
        .split("T")[0];
    } catch (e) {
      console.error("Error formatting delivery date:", e);
      formattedSelectedDate = new Date().toISOString().split("T")[0];
    }
  }

  // Handle payment click
  const handlePaymentClick = async () => {
    try {
     console.log("Selected Payment Mode:", selectedAddressId);
     
  const deliverySlotId =
    typeof selectedDeliverySlot === "object"
      ? selectedDeliverySlot?.id
      : selectedDeliverySlot;

      if (isLongDistance && !isCartEligibleForLongDistance) {
        setShowLongDistanceAmountDialog(true);
        return;
      }

      if (isLongDistance && !selectedAddressId) {
        dispatch(
          showSnackbar({
            message: "Please select a delivery address.",
            severity: "error",
            autoHide: 2000,
          })
        );
        return;
      }

       if (!isLongDistance &&!selectedAddressId) {
    dispatch(
      showSnackbar({
        message: "Please add a delivery address.",
        severity: "error",
        autoHide: 2000,
      })
    );
    return;
  }

  if (!isLongDistance &&!deliverySlotId &&  selectedDeliveryType !== "pickup") {
    dispatch(
      showSnackbar({
        message: "Please select a delivery slot.",
        severity: "error",
        autoHide: 2000,
      })
    );
    return;
  }

      // Validate additionalInfoPhoneNo if provided (must be 10 digits)
      setAdditionalInfoPhoneNoError("");
      if (additionalInfoPhoneNo && additionalInfoPhoneNo.trim() !== "") {
        const tenDigitMobileRegex = /^[6-9]\d{9}$/;
        if (!tenDigitMobileRegex.test(additionalInfoPhoneNo.trim())) {
          setAdditionalInfoPhoneNoError("Please enter a valid 10-digit mobile number.");
          dispatch(
            showSnackbar({ message: "Please enter a valid 10-digit mobile number.", severity: "error" })
          );
          return;
        }
      }
      const payload: any = {
        address_id: Number(selectedAddressId),
        // For long distance orders, we don't have pickup/delivery_slot/deliver_date from regular flow
        ...(isLongDistance ? {
          longdistance_delivery_mode: getDeliveryModeCode(selectedDeliveryMethod),
        } : {
          pickup: selectedDeliveryType === "pickup",
          deliver_date: formattedSelectedDate,
          ...(selectedDeliveryType === "pickup" && selectedPickupTime && {
            pickup_time: selectedPickupTime,
          }),
        }),
        opt_in: isLongDistance ? whatsappOptIn : optIn,
        delivery_mode: selectedPaymentModeId,
      };

      // Add additional information parameters if present
      if (isLongDistance && additionalInfoName) {
        payload.additional_info = additionalInfoName;
      } else if (addTocart?.additional_info) {
        payload.additional_info = addTocart.additional_info;
      }
      
      if (isLongDistance && additionalInfoPhoneNo) {
        payload.phone_no = additionalInfoPhoneNo;
      } else if (addTocart?.phone_no) {
        payload.phone_no = addTocart.phone_no;
      }

      if (!isLongDistance && !payload.pickup) {
        const deliverySlotId =
          selectedDeliverySlot && typeof selectedDeliverySlot === "object"
            ? selectedDeliverySlot.id
            : selectedDeliverySlot;
        payload.delivery_slot = Number(deliverySlotId);
      }

     const response: any = await dispatch(paymentCheckoutAsync(payload));
      console.log('Response Data:', response.payload.data);
      

      if (selectedPaymentModeId === 1 && response.payload.data) {
        dispatch(
          showSnackbar({
            message: "Order placed successfully with COD",
            severity: "success",
            autoHide: 3000,
          })
        );
        navigate("/order-confirmation");
      // } else if (typeof response.payload.data === "string" && response.payload.data.includes("payuForm")) {
      //   // Handle legacy HTML form response
      //   const tempContainer = document.createElement("div");
      //   tempContainer.innerHTML = response.payload.data;
      //   const form = tempContainer.querySelector("form[name='payuForm']") as HTMLFormElement;
        
      //   if (form) {
      //     document.body.appendChild(form);
      //     form.submit();
      //   } else {
      //     dispatch(
      //       showSnackbar({
      //         message: "Failed to initiate PayU payment",
      //         severity: "error",
      //       })
      //     );
      //   }
      } else if (
       response.payload.data?.status === 1 &&
       response.payload.data?.params &&
       response.payload.data?.params?.key
      ) {
        // Handle new JSON response format with payu_url and params
        console.log("PayU params found, redirecting...",response.payload.data.params);
        
        redirectToPayU(
         response.payload.data.params,
         response.payload.data.payu_url
        );
      } else {
        dispatch(
          showSnackbar({
            message: "Failed to initiate PayU payment",
            severity: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        showSnackbar({ message: "Something went wrong.", severity: "error" })
      );
    }
  };
const [openAddressPopup, setOpenAddressPopup] = useState(false);

const handleChangeAddress = () => {
  setOpenAddressPopup(true);
};
const handleCloseAddressPopup = () => {
  setOpenAddressPopup(false);
};

const toggleAlternateContact = () => setOpenAlternateContact(!openAlternateContact);
  return (
    <div className="d-flex flex-column">
      {isLongDistance ? (
        <>
            {isMobileScreen && (
                <>
                    <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <ArrowBackIcon
            onClick={() => dispatch(setStepperActiveMenu("address"))}
            style={{
              cursor: "pointer",
              marginLeft: "-36px", // Adjust as needed (-16px, -24px, etc.)
              marginRight: "8px",
              fontSize: "28px",
            }}
          />
        
          <h2
            style={{
              margin: 0,
              fontSize: "18px",
              
            }}
          >
          Payment & Delivery Options
          </h2>
        </div>
            {hasAddress ? (
              <SelectedAddressMobile
                address={selectedAddress}
                onChange={handleChangeAddress}
              />
            ) : (
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #E5E5E5",
                  borderRadius: "8px",
                  padding: "20px",
                  marginBottom: "16px",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "15px",
                  }}
                >
                  Please add your delivery address before proceeding to delivery options.
                </p>
                <CustomButton
                  name="Add Delivery Address"
                  onClick={handleChangeAddress}
                />
              </div>
            )}
            </>
          )}
           {!isMobileScreen && (
            <h2 style={{ fontSize: "18px", marginBottom: "20px" }}>Payment & Delivery Options</h2>
          )}

          {/* Choose Payment Method */}
          <h5 style={{ marginTop: "20px", marginBottom: "12px", fontSize: "14px" }}>
            1. Choose Payment Method
          </h5>
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              aria-label="payment-mode"
              name="payment-mode"
              value={
                selectedPaymentModeId !== null ? String(selectedPaymentModeId) : ""
              }
              onChange={(e) => {
                setSelectedPaymentModeId(Number(e.target.value));
              }}
            >
              {(addTocart?.available_payment_modes || []).map((mode: any) => (
                <FormControlLabel
                  key={mode.id}
                  value={String(mode.id)}
                  control={<Radio />}
                  label={mode.mode_name}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Choose Delivery Method */}
          <h5 style={{ marginTop: "20px", marginBottom: "12px", fontSize: "14px" }}>
            2. Delivery Address
          </h5>
          
          {/* Delivery Address Display */}
          {hasAddress&& (
            <fieldset className="box fieldset mt-3">
              <label htmlFor="note" style={{ paddingBottom: "1rem",fontWeight:" 500" }}>
                Your expected delivery within one week at the following address
              </label>{" "}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "16px",
              borderRadius: "4px",
              marginBottom: "20px",
              wordWrap: "break-word",
              overflowWrap: "break-word",
              minHeight: "auto",
            }}
          >
            <p
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: 0,
                marginBottom: "8px",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.5",
                color: "#ef8d22",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              <LocationOnIcon sx={{ fontSize: "20px" }} />
              {selectedAddress?.address_type || ""}
            </p>
            <p
              style={{
                margin: 0,
                wordWrap: "break-word",
                overflowWrap: "break-word",
                lineHeight: "1.5",
                fontWeight: 600,
                fontSize: "16px",
              }}
            >
              {displayAddress}
            </p>
          </div>
</fieldset>)}
{!hasAddress && isMobileScreen &&(<div className="box fieldset mt-3"/>)}
          {/* Additional Information Section - Collapsible on Mobile */}
          {isMobileScreen ? (
            <div className="mb-3">
              <p>
                <span onClick={toggleAlternateContact} style={{ cursor: "pointer" }}>
                  <i className={`fa fs-18 ${openAlternateContact ? 'fa-minus-circle' : 'fa-plus-circle'} green-icon`} style={{ color: "green" }}></i>{" "}
                  <b>Alternate Contact Details</b>
                </span>
              </p>
              {openAlternateContact && (
                <fieldset className="box fieldset mt-2">
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {/* Name Field */}
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "400" }}>Name</label>
                      <input
                        type="text"
                        placeholder="Enter Name"
                        value={additionalInfoName}
                        onChange={(e) => setAdditionalInfoName(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                    </div>

                    {/* Phone Number Field */}
                    <div>
                      <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "400" }}>Phone Number</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter Phone Number"
                        value={additionalInfoPhoneNo}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setAdditionalInfoPhoneNo(digits);
                          if (digits.length === 10) setAdditionalInfoPhoneNoError("");
                        }}
                        maxLength={10}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "14px",
                        }}
                      />
                      {additionalInfoPhoneNoError ? (
                        <p style={{ color: "#d32f2f", margin: "8px 0 0", fontSize: "12px" }}>
                          {additionalInfoPhoneNoError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </fieldset>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: "20px" }}>
              <h5 style={{ marginBottom: "12px", fontSize: "14px" }}>Alternate Contact Details</h5>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* Name Field */}
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "400" }}>Name</label>
                  <input
                    type="text"
                    placeholder="Enter Name"
                    value={additionalInfoName}
                    onChange={(e) => setAdditionalInfoName(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>

                {/* Phone Number Field */}
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: "400" }}>Phone Number</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter Phone Number"
                    value={additionalInfoPhoneNo}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setAdditionalInfoPhoneNo(digits);
                      if (digits.length === 10) setAdditionalInfoPhoneNoError("");
                    }}
                    maxLength={10}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                  {additionalInfoPhoneNoError ? (
                    <p style={{ color: "#d32f2f", margin: "8px 0 0", fontSize: "12px" }}>
                      {additionalInfoPhoneNoError}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp Opt-in */}
           <div
              className="box mb-3"
              style={{
                fontSize: "16px",
                lineHeight: "24px",
                color: "#000",
                fontFamily:"Roboto"
              }}
            >
              By continuing, you agree to receive order updates via WhatsApp.
            </div>

          <button
            className="tf-btn btn-fill animate-hover-btn radius-3 w-100 justify-content-center orange-b"
            onClick={handlePaymentClick}
            disabled={!selectedPaymentModeId || !selectedDeliveryMethod}
          >
            {selectedPaymentModeId == 1 ? "Confirm Order" : "Proceed to Pay"}
          </button>
        </>
      ) : (
        <>
          <h2 style={{ fontSize: "18px" }}>Choose Payment Method</h2>
          {/* Render available payment modes as radio buttons */}
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              aria-label="payment-mode"
              name="payment-mode"
              value={
                selectedPaymentModeId !== null ? String(selectedPaymentModeId) : ""
              }
              onChange={(e) => setSelectedPaymentModeId(Number(e.target.value))}
            >
              {(addTocart?.available_payment_modes || []).map((mode: any) => (
                <FormControlLabel
                  key={mode.id}
                  value={String(mode.id)}
                  control={<Radio />}
                  label={mode.mode_name}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {isMobileScreen && (
            <div
              className="box mb-3"
              style={{
                fontSize: "16px",
                lineHeight: "24px",
                color: "#000",
                fontFamily:"Roboto"
              }}
            >
              By continuing, you agree to receive order updates via WhatsApp.
            </div>
          )}
          <button
            className="tf-btn btn-fill animate-hover-btn radius-3 w-75 justify-content-center orange-b"
            onClick={handlePaymentClick}
            disabled={!selectedPaymentModeId}
          >
            {selectedPaymentModeId == 1 ? "Confirm Order" : "Proceed to Pay"}
          </button>
        </>
      )}
       <CustomDialog
                title="Select Delivery Address"
                openDialog={openAddressPopup}
                handleCloseDialog={handleCloseAddressPopup}
                component={
                    <DeliverAddress
                        isPopup={true}
                        onClose={handleCloseAddressPopup}
                    />
                }
                fullMobileWidth={true}
      />
     <CustomDialog
  title=""
  openDialog={showLongDistanceAmountDialog}
  handleCloseDialog={() => setShowLongDistanceAmountDialog(false)}
  modalPlace="center"
  component={
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "20px 20px 24px",
        fontFamily: "Albert Sans, sans-serif",
      }}
    >
      {/* Warning Icon */}
      <div
        style={{
          width: "70px",
          height: "70px",
          borderRadius: "50%",
          backgroundColor: "#ffdae0",
          color: "#d32f2f",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "40px",
          fontWeight: 700,
          lineHeight: 1,
          marginBottom: "22px",
          boxShadow: "inset 0 0 0 1px #ffd7dd",
        }}
      >
        !
      </div>

      <div
        style={{
          fontSize: "16px",
          fontWeight: 500,
          color: "#000",
          lineHeight: "1.6",
          maxWidth: "320px",
          marginBottom: "22px",
        }}
      >
       Add ₹300 or more to your cart to unlock delivery.
      </div>

      <button
        type="button"
        onClick={() => setShowLongDistanceAmountDialog(false)}
        style={{
          minWidth: "54px",
          height: "34px",
          padding: "0 16px",
          border: "none",
          borderRadius: "4px",
          backgroundColor: "#d32f2f",
          color: "#fff",
          fontSize: "12px",
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "Albert Sans, sans-serif",
        }}
      >
        OK
      </button>
    </div>
  }
/>
    </div>
  );
};
